import { SyntheticEvent, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { cart } from '@api/apis';
import debounce from 'lodash/debounce';
import Router from 'next/router';
import { IHash } from 'types/base.types';

/* App Notification Store */
import { updateAppNotification } from '@store/client/store.notifications';

/* utils */
import { Logger } from '@utils/logger';
import { StatusErrors } from '@constants/base';
import { deriveResponseError, deriveResponseStatus } from '@utils/object';
import { addSkuPath } from '@constants/paths';

interface ICartCanAddService {
  skuId?: number;
  options?: IHash<any>;
}

const useCartMutationCanAddService = ({ skuId, options = {} }: ICartCanAddService = {}) => {
  const mutation = useMutation({
    mutationFn: async (mutateSkuId?: number) => {
      const response = await cart.canAddService({ sku_id: skuId || mutateSkuId });

      if (StatusErrors.has(deriveResponseStatus(response)) || response.err) throw response;

      return {
        ...response,
        skuId: skuId || mutateSkuId,
      };
    },
    onSuccess: response => {
      if (response?.data?.result === true) {
        Router.push(addSkuPath(String(response.skuId)));
      }
    },
    onError: response => {
      const { errors, error_title: errorTitle } = deriveResponseError(response);

      updateAppNotification({ error_title: errorTitle, errors });
      Logger('useCartAddPlan')(response as Error);
    },
  });

  const debouncedMutate = useMemo(
    () =>
      debounce(
        (e?: SyntheticEvent, mutateSkuId?: number) => {
          e?.preventDefault();
          mutation.mutate(mutateSkuId);
        },
        1000,
        { trailing: false, leading: true },
      ),
    [mutation],
  );

  return { ...mutation, mutate: debouncedMutate };
};

export default useCartMutationCanAddService;
